import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { scheduleOnce } from '@ember/runloop';
import { tracked } from '@glimmer/tracking';

import onClickOutside from 'ember-click-outside/modifiers/on-click-outside';

import { AttachPosition, Direction, PopupCloseReason, PopupEvent } from 'webapp/enums/enums';
import { isAlive } from 'webapp/utils/common';
import { isElementOnScreen, isElementVisible } from 'webapp/utils/html';

import didInsert from 'webapp/modifiers/did-insert';
import onWindowEvents from 'webapp/modifiers/on-window-events';
import willDestroy from 'webapp/modifiers/will-destroy';
import type { NodeEventData, PopupWrapperBaseArgs } from 'webapp/components/popup/wrapper/base';
import PopupWrapperBaseComponent from 'webapp/components/popup/wrapper/base';

import type PopupContext from 'webapp/models-custom/popup/context';

interface Args extends PopupWrapperBaseArgs {
	model: PopupContext;
}

interface PopupWrapperContextComponentInterface<T> {
	Args: T;
	Blocks: { default: [] }; // this is needed for yield
}

export default class PopupWrapperContextComponent extends PopupWrapperBaseComponent<
	PopupWrapperContextComponentInterface<Args>
> {
	fullWidthMargin = 15; // also @fullWidthMargin in popups.less
	animateLength = 150;

	parentPopupNode: HTMLElement | null = null;

	@tracked isAttachedToElementOnScreen = true;

	@action
	checkCloseOnScroll(): void {
		// this was implemented because of the out-of-position popups when scrolling. link: https://app.asana.com/0/0/1202304498382746/f
		const popupContext = this.args.model;
		if (
			!popupContext.isClosing &&
			popupContext.isContext &&
			popupContext.attachTo?.offsetParent?.classList.contains('filterSwitcher')
		) {
			void popupContext.close(PopupCloseReason.CLOSEONBODYSCROLL);
		}
	}

	@action
	reposition(): void {
		const node = this.popupNode;

		if (!isAlive(this) || !node) {
			return;
		}

		const attachTo = this.args.model.attachTo;
		if (!attachTo || !isElementVisible(attachTo)) {
			if (!this.args.model.skipAttachToCheck) {
				void this.args.model.close(PopupCloseReason.NOATTACHTO);
			}
			return;
		}

		if (this.args.model.onBeforeReposition) {
			this.args.model.onBeforeReposition(node);
		}

		if (this.args.model.inheritWidth) {
			node.style.width = `${attachTo.offsetWidth}px`;
		}

		{
			const parentPopupNode = this.parentPopupNode;
			if (parentPopupNode) {
				const parentPopupNodeContext =
					parentPopupNode.querySelector<HTMLElement>('.content')!;

				if (parentPopupNodeContext?.contains(attachTo)) {
					this.isAttachedToElementOnScreen = isElementOnScreen(
						attachTo,
						parentPopupNodeContext,
					);
				}
			}
		}

		const contentNode = node.querySelector<HTMLElement>('.content')!,
			winH = window.innerHeight,
			winW = window.innerWidth,
			isFullWidth = !this.args.model.neverFullwidth && winW <= 600, // also @fullWidthContextBreakpoint in popups.less
			scrollT = window.scrollY,
			scrollL = window.scrollX,
			attachH = attachTo.offsetHeight,
			attachW = attachTo.offsetWidth,
			attachRect = attachTo.getBoundingClientRect(),
			minEdgePositionOffset = 20,
			maxEdgePositionOffset = 30,
			attachCenterPos = {
				top: scrollT + attachRect.top + attachH / 2,
				left: scrollL + attachRect.left + attachW / 2,
			},
			attachPosition = this.args.model.attachPosition,
			arrowNode = this.args.model.hideArrow
				? null
				: node.querySelector<HTMLElement>('.arrow')!,
			arrowSize = this.args.model.hideArrow ? 0 : 6,
			arrowOffset = this.args.model.offset,
			minEdgeDistance = isFullWidth ? 0 : 5;

		// clear previous constraints before measuring to get true natural dimensions.
		if (node.classList.contains('tooHigh')) {
			node.classList.remove('tooHigh');
			contentNode.style.maxHeight = '';
		}

		if (node.classList.contains('tooWide')) {
			node.classList.remove('tooWide');
			contentNode.style.maxWidth = '';
		}

		let popupW = node.offsetWidth,
			popupH = node.offsetHeight,
			isTooHigh = false,
			isTooWide = false;

		// direction priorities: preferred direction first
		let directions = Array.from(this.args.model.directions);

		if (isFullWidth) {
			directions = directions.filter((d) => ![Direction.LEFT, Direction.RIGHT].includes(d));
		}

		// first find the direction that fits
		let direction;
		directions.some((dir) => {
			let fits, top, left;

			if ([Direction.UP, Direction.DOWN].includes(dir)) {
				if (dir === Direction.UP) {
					top = attachRect.top - (popupH + arrowSize + arrowOffset);
					fits = top >= minEdgeDistance;
				}

				if (dir === Direction.DOWN) {
					top = attachRect.top + (attachH + arrowSize + arrowOffset);
					fits = winH - (top + popupH) >= minEdgeDistance;
				}
			} else {
				if (dir === Direction.RIGHT) {
					left = attachRect.left + (attachW + arrowSize + arrowOffset);
					fits = winW - (left + popupW) >= minEdgeDistance;
				}

				if (dir === Direction.LEFT) {
					left = attachRect.left - (popupW + arrowSize + arrowOffset);
					fits = left >= minEdgeDistance;
				}
			}

			if (fits) {
				direction = dir;
				return true;
			}

			return false;
		});

		if (!direction) {
			// no directions fit, what now?? resize popup to smaller!
			// pick the direction that has most space first
			const space: Partial<Record<Direction, number>> = {};

			if (directions.includes(Direction.UP)) {
				space.up = attachRect.top - scrollT;
			}
			if (directions.includes(Direction.DOWN)) {
				space.down = winH - space.up! - attachH;
			}
			if (directions.includes(Direction.LEFT)) {
				space.left = attachRect.left - scrollL;
			}
			if (directions.includes(Direction.RIGHT)) {
				space.right = winW - space.left! - attachW;
			}

			let max = 0;
			(Object.keys(space) as Direction[]).forEach((dir) => {
				if (space[dir]! > max) {
					direction = dir;
					max = space[dir]!;
				}
			});

			if (!direction) {
				return;
			}

			// set new popup size, mark class so we can add scrollbars
			const popupSpace = space[direction] - (minEdgePositionOffset + arrowSize + arrowOffset);

			if ([Direction.UP, Direction.DOWN].includes(direction)) {
				popupH = popupSpace;
				isTooHigh = true;
			} else {
				popupW = popupSpace;
				isTooWide = true;
			}
		}

		const attachPositionOffset = this.calculateAttachPositionOffset(
			direction,
			attachPosition,
			attachW,
			attachH,
		);

		// get top & left positions for the direction
		let top = 0,
			left = 0;
		if ([Direction.UP, Direction.DOWN].includes(direction)) {
			if (direction === Direction.UP) {
				top = scrollT + attachRect.top - (popupH + arrowSize + arrowOffset);
			}

			if (direction === Direction.DOWN) {
				top = scrollT + attachRect.top + (attachH + arrowSize + arrowOffset);
			}

			if (!isFullWidth) {
				const edgePositionOffset = Math.max(
					minEdgePositionOffset,
					Math.min(attachW / 2, maxEdgePositionOffset),
				);

				if (attachPosition === AttachPosition.START) {
					left = attachCenterPos.left - attachPositionOffset;
				} else if (attachPosition === AttachPosition.CENTER) {
					// this is the left position if we center the arrow
					left = attachCenterPos.left - popupW / 2;
				} else {
					left = attachCenterPos.left - popupW + attachPositionOffset;
				}

				// check if there is space to center the arrow
				if (left - scrollL >= minEdgeDistance) {
					const scrollBarWidth = window.innerWidth > document.body.clientWidth ? 20 : 0;

					if (winW - scrollBarWidth - (left - scrollL + popupW) < minEdgeDistance) {
						// no space on right, will extend popup from right edge to the left
						left = Math.min(
							attachCenterPos.left - (popupW - edgePositionOffset),
							winW + scrollL - minEdgeDistance - popupW,
						);
					}
				} else {
					// will extend popup from left edge to the right
					left = Math.max(
						attachCenterPos.left - edgePositionOffset,
						scrollL + minEdgeDistance,
					);
				}
			}
		} else {
			if (direction === Direction.RIGHT) {
				left = scrollL + attachRect.left + (attachW + arrowSize + arrowOffset);
			}

			if (direction === Direction.LEFT) {
				left = scrollL + attachRect.left - (popupW + arrowSize + arrowOffset);
			}

			const edgePositionOffset = Math.max(
				minEdgePositionOffset,
				Math.min(attachH / 2, maxEdgePositionOffset),
			);

			if (attachPosition === AttachPosition.START) {
				top = attachCenterPos.top - attachPositionOffset;
			} else if (attachPosition === AttachPosition.CENTER) {
				// this is the top position if we center the arrow
				top = attachCenterPos.top - popupH / 2;
			} else {
				top = attachCenterPos.top - popupH + attachPositionOffset;
			}

			// adjust position to keep popup within viewport, but only when attach element is visible.
			// when attach element scrolls out of view, skip adjustment so popup follows naturally.
			const isAttachInViewVertically = attachRect.top + attachH > 0 && attachRect.top < winH;

			if (isAttachInViewVertically) {
				if (top - scrollT >= minEdgeDistance) {
					if (winH - (top - scrollT + popupH) < minEdgeDistance) {
						// no space on bottom, will extend popup from bottom edge upwards
						top = Math.min(
							attachCenterPos.top - (popupH - edgePositionOffset),
							winH + scrollT - (minEdgeDistance + popupH),
						);
					}
				} else {
					// extend popup from attach point downwards
					top = attachCenterPos.top - edgePositionOffset;
				}
			}
		}

		if (isFullWidth) {
			left = this.fullWidthMargin;
		}

		// for LEFT/RIGHT directions, constrain height if popup overflows viewport vertically
		if (!isTooHigh && direction && [Direction.LEFT, Direction.RIGHT].includes(direction)) {
			const popupTopOnScreen = top - scrollT;
			const popupBottomOnScreen = popupTopOnScreen + popupH;
			const isAttachInView = attachRect.top + attachH > 0 && attachRect.top < winH;
			const overflowsTop = popupTopOnScreen < minEdgePositionOffset;
			const overflowsBottom = popupBottomOnScreen > winH - minEdgePositionOffset;

			if (overflowsTop || overflowsBottom) {
				isTooHigh = true;

				// when attach element is in view, clamp top to viewport so popup is fully visible.
				// when attach element scrolls out of view, let popup follow naturally (scroll-away).
				if (isAttachInView) {
					top = Math.max(scrollT + minEdgePositionOffset, top);
				}

				// constrain height to fit within viewport
				const effectiveTop = Math.max(top - scrollT, 0);
				const maxAvailableHeight = winH - effectiveTop - minEdgePositionOffset;
				popupH = Math.min(popupH, Math.max(0, maxAvailableHeight));
			}
		}

		node.style.top = `${top}px`;
		node.style.left = `${left}px`;
		node.classList.toggle('tooWide', isTooWide);
		node.classList.toggle('tooHigh', isTooHigh);
		node.classList.remove('d-up', 'd-down', 'd-left', 'd-right');
		node.classList.add(`d-${direction as Direction}`);

		contentNode.style.maxWidth = isTooWide ? `${popupW}px` : '';

		if (isTooHigh) {
			if ([Direction.LEFT, Direction.RIGHT].includes(direction)) {
				// popupH was already constrained above, use it directly
				contentNode.style.maxHeight = `${popupH}px`;
			} else {
				// UP/DOWN: calculate available space from attach element to viewport edge
				const availableSpace =
					direction === Direction.UP ? attachRect.top : winH - (attachRect.top + attachH);
				contentNode.style.maxHeight = `${Math.min(availableSpace - minEdgePositionOffset, popupH)}px`;
			}
		} else {
			contentNode.style.maxHeight = '';
		}

		if (typeof this.args.model.onChangeDirection === 'function') {
			this.args.model.onChangeDirection(direction);
		}

		// position the arrow
		if (arrowNode) {
			let arrowTop, arrowLeft;

			if ([Direction.UP, Direction.DOWN].includes(direction)) {
				arrowLeft = attachCenterPos.left - left - arrowSize;
			} else if (direction === Direction.LEFT) {
				arrowLeft = popupW - arrowSize;
			} else if (direction === Direction.RIGHT) {
				arrowLeft = -1 * arrowSize;
			}

			if ([Direction.LEFT, Direction.RIGHT].includes(direction)) {
				arrowTop = attachCenterPos.top - top - arrowSize;
			} else if (direction === Direction.UP) {
				arrowTop = popupH - arrowSize;
			} else if (direction === Direction.DOWN) {
				arrowTop = -1 * arrowSize;
			}

			arrowNode.style.top = `${arrowTop}px`;
			arrowNode.style.left = `${arrowLeft}px`;
		}
	}

	private bindNodeEvents(): void {
		const events: NodeEventData[] = [];

		const node = this.popupNode;
		if (node) {
			const eventData = {
				node,
				eventName: 'resize.contentchange',
				fn: () => {
					scheduleOnce('afterRender', this, this.reposition);
				},
			};
			events.push(eventData);
		}

		// designer animate handling - if attached to inside of the board, trigger reposition on animate end
		const viewBoardInnerNode = this.args.model.attachTo?.closest('.view-board-inner');
		if (viewBoardInnerNode) {
			const eventData = {
				node: viewBoardInnerNode as HTMLElement,
				eventName: 'custom.afterAnimate',
				fn: () => {
					this.reposition();
				},
			};
			events.push(eventData);
		}

		// ensure repositioning of context-popups on mobile app databoards subpage, app datawall, designer especially for mobile view
		const scrollableMobileViewBoardContentEl = document.querySelector(
			'.view-board--mobile .view-board-inner',
		);
		if (scrollableMobileViewBoardContentEl) {
			const eventData = {
				node: scrollableMobileViewBoardContentEl as HTMLElement,
				eventName: 'scroll',
				fn: () => {
					this.reposition();
				},
			};
			events.push(eventData);
		}

		// ensure repositioning of context-popups on designer block settings (right sidebar)
		const scrollableBlockSettingsNode = document.querySelector(
			'.bs-panel .designer-panel__inner-content',
		);
		if (scrollableBlockSettingsNode) {
			const eventData = {
				node: scrollableBlockSettingsNode as HTMLElement,
				eventName: 'scroll',
				fn: () => {
					this.reposition();
				},
			};
			events.push(eventData);
		}

		// ensure repositioning of context-popups on metric-insights drawer
		const attachTo = this.args.model.attachTo;
		if (attachTo) {
			const drawerNode = attachTo.closest('.popup-type-drawer');
			if (drawerNode) {
				// scrollable container inside the drawer
				const scrollable = drawerNode.querySelector('.container-main');
				if (scrollable) {
					const eventData = {
						node: scrollable as HTMLElement,
						eventName: 'scroll',
						fn: () => {
							this.reposition();
						},
					};
					events.push(eventData);
				}
			}
		}

		this.bindEvents(events);
	}

	private bindParentPopupEvents(): void {
		const attachTo = this.args.model.attachTo;

		if (!attachTo) {
			return;
		}

		const parentPopupNode =
			attachTo.closest('.modal-ct') || // if modal
			attachTo.closest('.context-popup'); // if context

		this.parentPopupNode = parentPopupNode as HTMLElement;

		if (!parentPopupNode) {
			return;
		}

		const events: NodeEventData[] = [];

		{
			const eventData = {
				node: parentPopupNode as HTMLElement,
				eventName: PopupEvent.CLOSING,
				fn: () => {
					if (this.args.model.isOpen) {
						this.close();
					}
				},
			};
			events.push(eventData);
		}

		{
			const contentNode = parentPopupNode.querySelector('.content');

			if (contentNode) {
				const eventData = {
					node: parentPopupNode as HTMLElement,
					eventName: 'scroll',
					fn: () => {
						this.reposition();
					},
				};
				events.push(eventData);
			}
		}

		this.bindEvents(events);
	}

	private calculateAttachPositionOffset(
		direction: Direction,
		attachPosition: AttachPosition,
		attachW: number,
		attachH: number,
	): number {
		if (!this.args.model.alignEdgeWithAttachTo) {
			return this.args.model.attachPositionOffset;
		}

		if (![AttachPosition.START, AttachPosition.END].includes(attachPosition)) {
			return this.args.model.attachPositionOffset;
		}

		return [Direction.UP, Direction.DOWN].includes(direction) ? attachW / 2 : attachH / 2;
	}

	@action
	override setupView(node: HTMLElement): void {
		super.setupView(node);
		this.popupNode = node;
		this.args.model.component = this;
		this.bindParentPopupEvents();
		this.bindNodeEvents();
		this.reposition();

		if (!this.args.model.isClosing) {
			this.readyToOpen();
		}
	}

	@action
	override onOutsideClick(e: Event): void {
		if (this.args.model.closeOnOutsideClickInModal) {
			super.onOutsideClick(e);
		}
	}
	<template>
		<div
			class="popup context-popup
				{{@model.themeClass}}
				{{@model.popupClass}}
				{{if @model.isAnimated 'animated'}}
				{{if @model.neverFullwidth 'skip-fullwidth'}}"
			{{didInsert this.setupView}}
			{{onWindowEvents this.reposition "resize scroll" 200}}
			{{onWindowEvents this.checkCloseOnScroll "scroll"}}
			{{willDestroy this.teardownView}}
		>
			{{#if @model.hasOverlay}}
				<div
					role="button"
					class="modal-overlay"
					{{on "click" this.handleOverlayClick}}
				></div>
			{{/if}}
			<div
				class="popup-inner
					{{unless this.isAttachedToElementOnScreen 'popup-inner--hidden'}}"
				{{onClickOutside this.onOutsideClick}}
			>
				{{#unless @model.hideArrow}}
					<small class="arrow"></small>
				{{/unless}}
				<div class="content">
					{{yield}}
				</div>
			</div>
		</div>
	</template>
}
